import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, formatDateIT } from "@/lib/api";
import { emitServicesChanged, onServicesChanged } from "@/lib/servicesSync";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import GenericInvoicedToggle from "@/components/GenericInvoicedToggle";
import { toast } from "sonner";
import { ClockCounterClockwise, ArrowClockwise, Plus, CurrencyEur, Trash, WarningCircle, CaretLeft, CaretRight, PencilSimple, FloppyDisk } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

const CATEGORY_LABELS = {
  antivirus: "Antivirus",
  dominio: "Dominio",
  hosting: "Hosting",
  email: "Email/PEC",
  backup: "Backup",
  abbonamento: "Abbonamento",
  pacchetto_assistenza: "Pacchetto assistenza",
  pacchetto_minuti: "Pacchetto minuti",
  altro: "Altro",
};

const PAGE_SIZE = 10;

export default function CustomerRenewalsHistory({ customerId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [editDialog, setEditDialog] = useState({ open: false, entry: null, price: "", notes: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/services-history`);
      setItems(data.items || []);
      setTotal(data.total_revenue || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [yearFilter]);

  // Real-time sync: reload when a service change happens elsewhere
  useEffect(() => {
    const off = onServicesChanged(customerId, () => { load(); });
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { off(); document.removeEventListener("visibilitychange", onVis); };
  }, [customerId, load]);

  const years = Array.from(new Set(items.map((it) => (it.date || "").slice(0, 4)).filter(Boolean))).sort().reverse();
  const filtered = yearFilter === "all"
    ? items
    : items.filter((it) => (it.date || "").startsWith(yearFilter));
  const filteredTotal = filtered.reduce((s, it) => s + (it.price || 0), 0);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const removeEntry = async (it) => {
    if (it.event === "creazione") {
      if (!window.confirm(
        `Eliminare completamente il servizio "${it.service_name}"?\n\n` +
        `Questa azione rimuove il servizio + TUTTO lo storico dei rinnovi + tutti i movimenti collegati. ` +
        `Il fatturato in dashboard verrà aggiornato di conseguenza.\n\n` +
        `Operazione irreversibile.`
      )) return;
      try {
        await api.delete(`/services/${it.service_id}?hard=true`);
        toast.success("Servizio eliminato completamente");
        emitServicesChanged(customerId);
        load();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } else if (it.event === "rinnovo") {
      if (!window.confirm(`Annullare questo rinnovo di ${formatDateIT((it.date || "").slice(0, 10))} da ${eur(it.price)}?\nL'operazione verrà rimossa dallo storico e dal fatturato.`)) return;
      try {
        await api.delete(`/services/${it.service_id}/history/${it.history_index}`);
        toast.success("Rinnovo eliminato dallo storico");
        emitServicesChanged(customerId);
        load();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
  };

  const openEdit = (it) => {
    setEditDialog({
      open: true,
      entry: it,
      price: it.price != null ? String(it.price) : "",
      notes: it.notes || "",
    });
  };

  const saveEdit = async () => {
    const { entry } = editDialog;
    if (!entry) return;
    const priceNum = editDialog.price === "" ? null : parseFloat(editDialog.price);
    if (priceNum != null && isNaN(priceNum)) return toast.error("Prezzo non valido");
    setSavingEdit(true);
    try {
      const payload = { price: priceNum, notes: editDialog.notes.trim() || null };
      if (entry.event === "creazione") {
        // Update the service itself → propagates everywhere
        await api.patch(`/services/${entry.service_id}`, payload);
      } else {
        // Update the specific history entry
        await api.patch(`/services/${entry.service_id}/history/${entry.history_index}`, payload);
      }
      toast.success("Prezzo aggiornato");
      emitServicesChanged(customerId);
      setEditDialog({ open: false, entry: null, price: "", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Card className="p-6 border-border/70 rounded-lg" data-testid="customer-renewals-history">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={20} weight="duotone" className="text-primary" />
          <div>
            <h2 className="font-display font-bold tracking-tight text-lg">Storico rinnovi & vendite</h2>
            <div className="text-xs text-muted-foreground">
              {filtered.length} event{filtered.length === 1 ? "o" : "i"} · Totale{" "}
              <span className="font-mono font-semibold text-foreground">{eur(filteredTotal)}</span>
              {yearFilter !== "all" && (
                <span> · anno {yearFilter}</span>
              )}
            </div>
          </div>
        </div>
        {years.length > 0 && (
          <div className="flex gap-1 bg-secondary rounded-md p-1 flex-wrap">
            <button
              onClick={() => setYearFilter("all")}
              data-testid="history-year-all"
              className={`px-3 py-1 rounded text-xs font-mono ${yearFilter === "all" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              style={{ transition: "background-color 0.15s ease" }}
            >
              Tutti
            </button>
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setYearFilter(y)}
                data-testid={`history-year-${y}`}
                className={`px-3 py-1 rounded text-xs font-mono ${yearFilter === y ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                style={{ transition: "background-color 0.15s ease" }}
              >
                {y}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Caricamento…</div>}
      {!loading && filtered.length === 0 && (
        <div className="text-sm text-muted-foreground/70 font-mono border border-dashed border-border rounded-md p-6 text-center">
          — Nessun rinnovo o vendita registrata —
        </div>
      )}

      <div className="space-y-2">
        {pageItems.map((it) => {
          const patchUrl = it.history_index !== null && it.history_index !== undefined
            ? `/services/${it.service_id}/history/${it.history_index}`
            : `/services/${it.service_id}`;
          const isMinutePackage = it.category === "pacchetto_minuti";
          return (
            <div
              key={it.id}
              className={`flex flex-col sm:flex-row sm:items-center gap-3 justify-between border rounded-md px-3 py-2.5 hover:bg-secondary/40 ${it.service_deleted ? "border-dashed border-muted-foreground/30 bg-muted/20 opacity-90" : "border-border/60"}`}
              style={{ transition: "background-color 0.15s ease" }}
              data-testid={`history-row-${it.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {it.event === "creazione" ? (
                    <Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/30 hover:bg-blue-500/15 font-mono text-[9px] uppercase gap-1">
                      <Plus size={10} weight="bold" /> Creazione
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/15 font-mono text-[9px] uppercase gap-1">
                      <ArrowClockwise size={10} weight="bold" /> Rinnovo
                    </Badge>
                  )}
                  <div className="font-medium">{it.service_name}</div>
                  {it.category && (
                    <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase">
                      {CATEGORY_LABELS[it.category] || it.category}
                    </Badge>
                  )}
                  {it.service_deleted && (
                    <Badge className="bg-muted text-muted-foreground border border-muted-foreground/40 hover:bg-muted font-mono text-[9px] uppercase gap-1">
                      <WarningCircle size={10} weight="bold" /> Servizio eliminato
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground font-mono mt-1 flex items-center gap-3 flex-wrap">
                  <span>Data: {formatDateIT((it.date || "").slice(0, 10))}</span>
                  {it.price > 0 && <span className="inline-flex items-center gap-1"><CurrencyEur size={11} /> {eur(it.price)}</span>}
                  {!isMinutePackage && it.new_expires_at && (
                    <span>Nuova scadenza: {formatDateIT(it.new_expires_at)}</span>
                  )}
                  {isMinutePackage && it.minutes_purchased && (
                    <span>
                      Acquistati <span className="font-semibold text-foreground">{it.minutes_purchased} min</span>
                      {it.residual_minutes != null && <> + {it.residual_minutes} residui</>}
                      {it.minutes_total_after && <> = <span className="font-semibold text-foreground">{it.minutes_total_after} disponibili</span></>}
                    </span>
                  )}
                  {it.by_user && <span className="opacity-70">by {it.by_user}</span>}
                </div>
                {it.notes && <div className="text-xs text-muted-foreground mt-1">Note: {it.notes}</div>}
              </div>
              <div className="shrink-0 flex items-center gap-1">
                <GenericInvoicedToggle
                  key={`${it.id}-${it.invoiced}`}
                  initialInvoiced={it.invoiced}
                  patchUrl={patchUrl}
                  testId={`history-invoiced-${it.id}`}
                  onChange={() => setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, invoiced: !x.invoiced } : x))}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEdit(it)}
                  data-testid={`history-edit-${it.id}`}
                  title="Modifica prezzo / note"
                >
                  <PencilSimple size={13} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeEntry(it)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  data-testid={`history-delete-${it.id}`}
                  title={it.event === "creazione" ? "Elimina definitivamente il servizio + tutto lo storico" : "Annulla questo rinnovo"}
                >
                  <Trash size={13} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {!loading && totalPages > 1 && (
        <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between" data-testid="history-pagination">
          <div className="font-mono text-[10px] text-muted-foreground">
            Pagina {currentPage} di {totalPages} · {pageStart + 1}-{Math.min(pageStart + PAGE_SIZE, filtered.length)} di {filtered.length}
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="history-page-prev"
              className="h-7 w-7 p-0 rounded"
            ><CaretLeft size={13} /></Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="history-page-next"
              className="h-7 w-7 p-0 rounded"
            ><CaretRight size={13} /></Button>
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border/50 flex justify-between font-mono text-xs">
          <span className="text-muted-foreground">Totale storico completo</span>
          <span className="font-bold text-foreground">{eur(total)}</span>
        </div>
      )}

      <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, entry: null, price: "", notes: "" })}>
        <DialogContent data-testid="history-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple size={20} weight="duotone" className="text-primary" />
              Modifica {editDialog.entry?.event === "creazione" ? "servizio" : "rinnovo"}
            </DialogTitle>
            <DialogDescription>
              {editDialog.entry?.service_name} · {formatDateIT((editDialog.entry?.date || "").slice(0, 10))}
              {editDialog.entry?.event === "creazione" && (
                <span className="block mt-1 text-[11px]">Modificare qui cambia il prezzo del servizio ovunque appare (Scadenzario, Storico cliente, Dashboard).</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Prezzo (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={editDialog.price}
                onChange={(e) => setEditDialog({ ...editDialog, price: e.target.value })}
                data-testid="history-edit-price"
                className="mt-1.5 font-mono"
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Note</Label>
              <Input
                value={editDialog.notes}
                onChange={(e) => setEditDialog({ ...editDialog, notes: e.target.value })}
                data-testid="history-edit-notes"
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog({ open: false, entry: null, price: "", notes: "" })}>Annulla</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="history-edit-save-btn" className="rounded-full gap-2">
              <FloppyDisk size={14} weight="bold" /> {savingEdit ? "Salvo…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
